import { useState, useEffect, useCallback, useRef } from 'react';
import {
  readWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  updateSnapshot,
  WatchedDomain,
  WatchedSnapshot,
} from '@/lib/watchlistStorage';
import { scanApi } from '@/services/api';
import { DomainScanResult } from '@/types';

function resultToSnapshot(r: DomainScanResult): WatchedSnapshot {
  const risk = r.brandRisk?.riskLevel;
  return {
    available: r.status === 'available' ? true : r.status === 'registered' ? false : null,
    priceUsd: r.pricing?.purchasePrice ?? null,
    registrar: r.pricing?.registrar ?? r.whoisData?.registrar ?? null,
    valuation: r.valuation?.estimatedValue?.min ?? null,
    brandRiskState:
      risk === 'low' ? 'clear' :
      risk === 'medium' ? 'caution' :
      risk === 'high' ? 'risk' : 'unknown',
  };
}

export type ChangeKind = 'price_drop' | 'now_unavailable' | 'now_available';

export type DomainChange = {
  domain: string;
  kind: ChangeKind;
  from: string;
  to: string;
};

export function useWatchlist() {
  const [items, setItems] = useState<WatchedDomain[]>(() => readWatchlist());
  const [checking, setChecking] = useState<Set<string>>(new Set());
  const [changes, setChanges] = useState<DomainChange[]>([]);
  const firstWatchSeenKey = 'dombase:onboarding:firstWatchSeen';
  const [showFirstWatchTip, setShowFirstWatchTip] = useState(false);
  const recheckQueueRef = useRef<string[]>([]);
  const recheckActiveRef = useRef(0);
  const CONCURRENCY = 5;

  // Sync across tabs via storage events
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'dombase:watchlist:v1') {
        setItems(readWatchlist());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const refresh = useCallback(() => setItems(readWatchlist()), []);

  const isWatchedLocal = useCallback(
    (domain: string) => items.some(i => i.domain === domain),
    [items]
  );

  const toggle = useCallback(
    (domain: string, snapshot: WatchedSnapshot): { ok: boolean; reason?: string } => {
      if (items.some(i => i.domain === domain)) {
        removeFromWatchlist(domain);
        refresh();
        return { ok: true };
      }
      const result = addToWatchlist(domain, snapshot);
      if (result.ok) {
        refresh();
        // First-watch onboarding tip
        if (!localStorage.getItem(firstWatchSeenKey)) {
          setShowFirstWatchTip(true);
          localStorage.setItem(firstWatchSeenKey, '1');
          setTimeout(() => setShowFirstWatchTip(false), 4000);
        }
      }
      return result;
    },
    [items, refresh]
  );

  const dismissFirstWatchTip = useCallback(() => setShowFirstWatchTip(false), []);

  const recheckOne = useCallback(async (domain: string) => {
    setChecking(prev => new Set(prev).add(domain));
    try {
      const result = await scanApi.scanSingle(domain);
      const newSnap = resultToSnapshot(result);
      const old = items.find(i => i.domain === domain)?.snapshot;

      // Detect changes
      if (old) {
        const newChanges: DomainChange[] = [];
        if (old.available === true && newSnap.available === false) {
          newChanges.push({ domain, kind: 'now_unavailable', from: 'available', to: 'unavailable' });
        }
        if (old.available === false && newSnap.available === true) {
          newChanges.push({ domain, kind: 'now_available', from: 'unavailable', to: 'available' });
        }
        if (
          old.priceUsd !== null &&
          newSnap.priceUsd !== null &&
          newSnap.priceUsd < old.priceUsd
        ) {
          newChanges.push({
            domain,
            kind: 'price_drop',
            from: `$${old.priceUsd.toFixed(2)}`,
            to: `$${newSnap.priceUsd.toFixed(2)}`,
          });
        }
        if (newChanges.length) {
          setChanges(prev => {
            const withoutDomain = prev.filter(c => c.domain !== domain);
            return [...withoutDomain, ...newChanges];
          });
        }
      }

      updateSnapshot(domain, newSnap);
      refresh();
    } catch {
      // ignore individual recheck failures
    } finally {
      setChecking(prev => {
        const next = new Set(prev);
        next.delete(domain);
        return next;
      });
    }
  }, [items, refresh]);

  const drainQueue = useCallback(async () => {
    while (recheckQueueRef.current.length > 0 && recheckActiveRef.current < CONCURRENCY) {
      const domain = recheckQueueRef.current.shift()!;
      recheckActiveRef.current++;
      recheckOne(domain).finally(() => {
        recheckActiveRef.current--;
        drainQueue();
      });
    }
  }, [recheckOne]);

  const recheckAll = useCallback((domains?: string[]) => {
    const toCheck = domains ?? items.map(i => i.domain);
    recheckQueueRef.current = [...toCheck];
    drainQueue();
  }, [items, drainQueue]);

  // Auto-recheck on mount — skip domains checked within the last 2 hours
  const autoCheckedRef = useRef(false);
  useEffect(() => {
    if (autoCheckedRef.current || items.length === 0) return;
    autoCheckedRef.current = true;
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    const stale = [...items]
      .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
      .slice(0, 10)
      .filter(i => Date.now() - new Date(i.lastChecked).getTime() > TWO_HOURS)
      .map(i => i.domain);
    if (stale.length > 0) recheckAll(stale);
  }, []); // intentionally empty — runs once on mount

  return {
    items,
    isWatched: isWatchedLocal,
    toggle,
    recheckOne,
    recheckAll,
    checking,
    changes,
    showFirstWatchTip,
    dismissFirstWatchTip,
  };
}
